import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PublicLayout } from '@/components/layout'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { getGameDetail, getGameRewardStatus, claimGameReward, resolvePlayUrl } from './api'
import type { Game } from './types'

// 游戏播放页
export function GamePlayer() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { gameId } = useParams({ from: '/games/$gameId/' })
  const queryClient = useQueryClient()
  const [showRewardBanner, setShowRewardBanner] = useState(false)
  const [pendingScore, setPendingScore] = useState(0)

  // 获取游戏详情
  const { data: gameResp, isLoading } = useQuery({
    queryKey: ['game', gameId],
    queryFn: () => getGameDetail(gameId),
  })
  const game = gameResp?.data

  // 获取奖励状态
  const { data: rewardStatus } = useQuery({
    queryKey: ['game-reward-status', game?.slug],
    queryFn: () => getGameRewardStatus(game!.slug),
    enabled: !!game?.slug && game.reward_quota > 0,
  })

  // 领取奖励
  const claimMutation = useMutation({
    mutationFn: (score: number) => claimGameReward(game!.slug, score),
    onSuccess: (res) => {
      if (res.success && res.data) {
        toast.success(`${res.data.message} (+${res.data.reward_quota} 额度)`)
        setShowRewardBanner(false)
        queryClient.invalidateQueries({ queryKey: ['game-reward-status', game?.slug] })
      } else {
        toast.error(res.message || '领取失败')
      }
    },
    onError: () => {
      toast.error('网络错误，请重试')
    },
  })

  // 监听 iframe postMessage
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'game-complete') {
        const { gameSlug, score } = event.data
        if (gameSlug === game?.slug) {
          setPendingScore(score || 0)
          setShowRewardBanner(true)
        }
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [game?.slug])

  const handleClaim = useCallback(() => {
    if (game && rewardStatus?.data?.can_claim) {
      claimMutation.mutate(pendingScore)
    }
  }, [game, rewardStatus, pendingScore, claimMutation])

  const handleBack = () => {
    navigate({ to: '/games' })
  }

  if (isLoading) {
    return (
      <PublicLayout>
        <div className='container mx-auto max-w-4xl px-4 py-8'>
          <Skeleton className='mb-4 h-12' />
          <Skeleton className='h-[600px]' />
        </div>
      </PublicLayout>
    )
  }

  if (!game) {
    return (
      <PublicLayout>
        <div className='container mx-auto max-w-4xl px-4 py-8'>
          <p className='text-center text-muted-foreground'>{t('游戏不存在')}</p>
          <div className='mt-4 text-center'>
            <Button onClick={handleBack}>{t('返回游戏库')}</Button>
          </div>
        </div>
      </PublicLayout>
    )
  }

  const playUrl = resolvePlayUrl(game)
  const isMarkdown = game.play_type === 'markdown'

  return (
    <PublicLayout>
      <div className='container mx-auto max-w-5xl px-4 py-6'>
        {/* 顶部栏 */}
        <div className='mb-4 flex items-center justify-between'>
          <button
            onClick={handleBack}
            className='flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground'
          >
            ← {t('返回游戏库')}
          </button>
          <h1 className='flex items-center gap-2 text-lg font-semibold'>
            <span className='text-2xl'>{game.icon}</span>
            {game.name}
          </h1>
          <div className='w-24' />
        </div>

        {/* 奖励横幅 */}
        {game.reward_quota > 0 && rewardStatus?.data && (
          <div className='mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <span className='text-xl'>🎁</span>
                <div>
                  <p className='text-sm font-medium'>
                    {t('奖励')}: +{game.reward_quota} {t('额度')}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {game.reward_condition}
                  </p>
                </div>
              </div>
              <div className='text-right text-xs text-muted-foreground'>
                <p>{t('今日已领')}: {rewardStatus.data.claimed_today}/{rewardStatus.data.reward_daily_limit}</p>
              </div>
            </div>
          </div>
        )}

        {/* 游戏完成后的领取弹窗 */}
        {showRewardBanner && game.reward_quota > 0 && rewardStatus?.data?.can_claim && (
          <div className='mb-4 rounded-xl border border-green-500/30 bg-green-500/10 p-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <span className='text-2xl'>🎉</span>
                <div>
                  <p className='font-medium text-green-700 dark:text-green-400'>
                    {t('游戏完成！可以领取奖励')}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {t('得分')}: {pendingScore}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleClaim}
                disabled={claimMutation.isPending}
                size='sm'
              >
                {claimMutation.isPending ? t('领取中...') : t('领取奖励')}
              </Button>
            </div>
          </div>
        )}

        {/* 游戏内容区 */}
        {isMarkdown ? (
          <div className='rounded-xl border bg-card p-6'>
            <div className='prose prose-sm dark:prose-invert max-w-none'>
              <pre className='whitespace-pre-wrap font-sans text-sm leading-relaxed'>
                {game.description}
              </pre>
            </div>
          </div>
        ) : playUrl ? (
          <div className='overflow-hidden rounded-xl border shadow-lg'>
            <iframe
              src={playUrl}
              title={game.name}
              className='h-[70vh] w-full'
              sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-modals'
              allow='fullscreen'
            />
          </div>
        ) : (
          <div className='rounded-xl border bg-card p-6 text-center text-muted-foreground'>
            {t('游戏加载失败')}
          </div>
        )}

        {/* 游戏信息 */}
        <div className='mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground'>
          {game.developer && <span>{t('开发者')}: {game.developer}</span>}
          <span>{t('播放')}: {game.play_count}</span>
          {game.tags && (
            <div className='flex flex-wrap gap-1'>
              {game.tags.split(',').map((tag) => (
                <span key={tag} className='rounded bg-muted px-2 py-0.5'>
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  )
}
